// The configured choices lane B3's screens offer, read from B1's `config.options`: the Admin's published taxonomies
// (`taxonomies:source`, `taxonomies:segment`, `taxonomies:mobility_type`) and reason lists (`reasons:application_hold`,
// `reasons:withdrawal`, `reasons:pass`), and the people a picker offers from `people.options` (the active holders of
// one role, projected to id, name, initials and accent). A list is empty while it loads and when it is refused or not yet published, so a control that
// needs one shows nothing rather than a guess; the server validates every slug it is sent and stamps the version it used.

import { useMemo } from "react";
import { configOptions, peopleOptions, type ConfigOptionsInput, type PeopleOptionsInput } from "@/api/tools";
import { useSettledRead } from "@/views/shared/settledRead";

export interface Choice {
  value: string;
  label: string;
}

type ListKind = ConfigOptionsInput["kind"];

/** One published list's active choices (empty while loading or refused); `enabled` false reads nothing. */
export function useOptions(kind: ListKind, name: string, enabled = true): readonly Choice[] {
  const read = useSettledRead(["config-options", kind, name], () => configOptions({ kind, name }), {
    enabled,
    staleTime: 5 * 60_000,
  });
  return useMemo(() => (read.data?.options ?? []).map((option) => ({ value: option.slug, label: option.label })), [read.data]);
}

/** The word a list gives a value (the value itself when the list does not carry it, or has not loaded). */
export const wordIn = (list: readonly Choice[], value: string | null | undefined): string | null =>
  value ? (list.find((choice) => choice.value === value)?.label ?? value) : null;

export const useSourceChoices = (enabled = true) => useOptions("taxonomies", "source", enabled);
export const useSegmentChoices = (enabled = true) => useOptions("taxonomies", "segment", enabled);
export const useMobilityTypeChoices = (enabled = true) => useOptions("taxonomies", "mobility_type", enabled);
export const useHoldReasonChoices = (enabled = true) => useOptions("reasons", "application_hold", enabled);
export const useWithdrawalReasonChoices = (enabled = true) => useOptions("reasons", "withdrawal", enabled);
/** The disposition reasons a Pass may carry ("Pass cannot finish without disposition reason."). */
export const usePassReasonChoices = (enabled = true) => useOptions("reasons", "pass", enabled);

export type PickRole = PeopleOptionsInput["role"];

export interface PersonChoice extends Choice {
  initials: string;
  hue: string;
}

/** The active holders of one role a picker offers (empty while loading or refused), with whether it is still loading.
 *  Choosing grants nothing: the write that records the choice re-checks its rule (`owner_not_recruiter`). */
export function usePeopleChoices(role: PickRole, enabled = true): { choices: readonly PersonChoice[]; isLoading: boolean } {
  const read = useSettledRead(["people-options", role], () => peopleOptions({ role }), { enabled, staleTime: 5 * 60_000 });
  const choices = useMemo(
    () =>
      (read.data?.options ?? []).map((person) => ({
        value: person.person_id,
        label: person.name,
        initials: person.initials,
        hue: person.hue,
      })),
    [read.data],
  );
  return { choices, isLoading: enabled && read.isLoading };
}

/** The source word of a profile or card (`taxonomies:source`), as a function of the slug. */
export function useSourceWord(): (slug: string | null | undefined) => string {
  const choices = useSourceChoices();
  return (slug) => wordIn(choices, slug) ?? "";
}
